/**
 * Cerrar la caja: contar la plata de los cajones.
 *
 * EL CONTEO ES A CIEGAS, Y ES TODO EL PUNTO
 * -----------------------------------------
 * Este diálogo NO muestra cuánto debería haber hasta después de anotar. No es
 * un descuido de diseño: un arqueo donde el número esperado está a la vista
 * deja de ser un control y pasa a ser un trámite, porque se tipea lo que dice
 * la pantalla. Es la razón por la que `registrar_arqueo` calcula el esperado en
 * la base en vez de aceptarlo del cliente (migración 0069).
 *
 * UN SOLO PASO PARA LOS DOS CAJONES (0155)
 * ----------------------------------------
 * Al cerrar se cuentan el Efectivo en pesos y el de dólares juntos (y, si hace
 * falta, otra cuenta). Antes eran dos diálogos, y en noviembre nadie contó la
 * caja ni una vez: un sobrante terminó cargado a mano como «Otro ingreso».
 *
 * ANTES DE ANOTAR SE CONFIRMA LO TIPEADO, SIN MOSTRAR EL SISTEMA
 * ---------------------------------------------------------------
 * Un cero de menos («120.000» en vez de «1.200.000») quedaba asentado como un
 * faltante de un millón que pesaba en Ganancias. Por eso, antes de anotar, se
 * repite en grande lo contado: «Contaste $ 120.000 · ¿está bien?». Lo que dice
 * el sistema y la diferencia siguen apareciendo recién después (decisión del
 * dueño, 05/11).
 *
 * LA DIFERENCIA NO SE TAPA
 * ------------------------
 * Si no coincide, la base asienta un movimiento propio (`AJUSTE_ARQUEO`).
 * Después del conteo el saldo del sistema es el que se contó, pero queda
 * escrito cuánto faltó y cuándo.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ClipboardCheck } from "lucide-react";

import { formatear, type Moneda } from "@tora/shared";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Campo } from "@/components/catalogo/Campos";
import { Importe } from "@/components/money/importe";
import { InputImporte } from "@/components/money/input-importe";
import { SelectNativo } from "@/components/ui/select-nativo";
import { Textarea } from "@/components/ui/textarea";
import type { CuentaFinanciera } from "@/components/cobro/logica";

/** Lo que devuelve el backend por cada cuenta contada. */
export interface ResultadoArqueo {
  cuenta: string;
  moneda: Moneda;
  contado: number | string;
  esperado: number | string;
  diferencia: number | string;
}

export interface CierreCaja {
  conteos: Array<{ cuenta_id: number; contado: number }>;
  observaciones?: string;
}

export interface DialogArqueoProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  cuentas: CuentaFinanciera[];
  guardando?: boolean;
  /** Lo que resultó de cada cuenta contada, o null mientras no se anotó. */
  resultado: ResultadoArqueo[] | null;
  onConfirmar: (datos: CierreCaja) => void;
  onCerrar: () => void;
}

const n = (v: number | string): number => (typeof v === "number" ? v : Number(v));

const nombreCon = (c: CuentaFinanciera): string =>
  `${c.nombre} (${c.moneda === "ARS" ? "pesos" : "dólares"})`;

export function DialogArqueo({
  abierto,
  onAbiertoChange,
  cuentas,
  guardando = false,
  resultado,
  onConfirmar,
  onCerrar,
}: DialogArqueoProps): JSX.Element {
  const activas = useMemo(() => cuentas.filter((c) => c.activo !== false), [cuentas]);
  // Los cajones de efectivo van siempre; otra cuenta se suma si hace falta.
  const cajones = useMemo(() => activas.filter((c) => c.tipo === "CAJA"), [activas]);
  const [extras, setExtras] = useState<number[]>([]);
  const [contados, setContados] = useState<Record<number, number | null>>({});
  const [observaciones, setObservaciones] = useState("");
  const [paso, setPaso] = useState<"contar" | "confirmar">("contar");

  useEffect(() => {
    if (!abierto) return;
    setExtras([]);
    setContados({});
    setObservaciones("");
    setPaso("contar");
  }, [abierto]);

  // Sin cajones de efectivo (una base rara) se cuenta la primera cuenta activa.
  const base = cajones.length > 0 ? cajones : activas.slice(0, 1);
  const filas = [
    ...base,
    ...extras
      .map((id) => activas.find((c) => c.id === id))
      .filter((c): c is CuentaFinanciera => c !== undefined && !base.some((b) => b.id === c.id)),
  ];
  const otras = activas.filter((c) => !filas.some((f) => f.id === c.id));
  const conteos = filas
    .map((c) => ({ cuenta: c, contado: contados[c.id] ?? null }))
    .filter(
      (x): x is { cuenta: CuentaFinanciera; contado: number } =>
        x.contado !== null && x.contado >= 0,
    );
  const puede = conteos.length > 0 && !guardando;

  function anotar(): void {
    onConfirmar({
      conteos: conteos.map((x) => ({ cuenta_id: x.cuenta.id, contado: x.contado })),
      ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
    });
  }

  // Ya se anotó: recién ahora se ve contra qué.
  if (resultado) {
    const hayDiferencia = resultado.some((r) => Math.abs(n(r.diferencia)) >= 0.01);
    return (
      <Dialog open={abierto} onOpenChange={(a) => !a && onCerrar()}>
        <DialogContent tamano="sm" mobile="hoja">
          <DialogHeader>
            <DialogTitle>Caja cerrada</DialogTitle>
            <DialogDescription>
              {hayDiferencia ? "No cerró exacto. La diferencia quedó asentada." : "Cerró exacto."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {resultado.map((r) => {
              const dif = n(r.diferencia);
              return (
                <dl key={`${r.cuenta}-${r.moneda}`} className="space-y-1.5 text-sm">
                  <dt className="font-medium">
                    {r.cuenta} ({r.moneda === "ARS" ? "pesos" : "dólares"})
                  </dt>
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted-foreground">Contaste</dt>
                    <dd>
                      <Importe monto={n(r.contado)} moneda={r.moneda} />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted-foreground">El sistema decía</dt>
                    <dd>
                      <Importe monto={n(r.esperado)} moneda={r.moneda} />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5">
                    <dt>{Math.abs(dif) < 0.01 ? "Exacto" : dif > 0 ? "Sobró" : "Faltó"}</dt>
                    <dd>
                      <Importe monto={dif} moneda={r.moneda} />
                    </dd>
                  </div>
                </dl>
              );
            })}
          </div>

          {hayDiferencia ? (
            <Aviso tono="info">
              El saldo del sistema pasa a ser lo que contaste, que es lo que hay. La diferencia no
              se borra: quedó como un movimiento propio, para que se pueda ver si vuelve a pasar.
            </Aviso>
          ) : null}

          <DialogFooter>
            <Button type="button" onClick={onCerrar}>
              Listo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (paso === "confirmar") {
    return (
      <Dialog open={abierto} onOpenChange={onAbiertoChange}>
        <DialogContent tamano="sm" mobile="hoja">
          <DialogHeader>
            <DialogTitle>¿Está bien lo que contaste?</DialogTitle>
            <DialogDescription>
              Mirá bien los números: un cero de más o de menos queda anotado como faltante.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-3">
            {conteos.map(({ cuenta, contado }) => (
              <li key={cuenta.id} className="text-center">
                <p className="text-sm text-muted-foreground">{nombreCon(cuenta)}</p>
                <p className="text-2xl font-semibold tabular-nums">
                  Contaste {formatear(contado, cuenta.moneda)}
                </p>
              </li>
            ))}
          </ul>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPaso("contar")}>
              <ArrowLeft aria-hidden="true" />
              Me equivoqué
            </Button>
            <Button type="button" cargando={guardando} onClick={anotar}>
              Sí, anotarlo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="sm" mobile="hoja">
        <DialogHeader>
          <DialogTitle>Cerrar la caja</DialogTitle>
          <DialogDescription>
            Contá la plata de cada cajón y escribila. No te mostramos cuánto debería haber hasta
            después: si lo ves antes, el conteo no sirve de control.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {filas.map((c) => (
            <Campo key={c.id} label={`Cuánto hay en ${nombreCon(c)}`} id={`arqueo-${c.id}`}>
              <InputImporte
                id={`arqueo-${c.id}`}
                valor={contados[c.id] ?? null}
                moneda={c.moneda}
                onValorChange={(v) => setContados((prev) => ({ ...prev, [c.id]: v }))}
              />
            </Campo>
          ))}

          {otras.length > 0 ? (
            <Campo label="Contar otra cuenta" id="arqueo-otra" ayuda="Opcional.">
              <SelectNativo
                id="arqueo-otra"
                value=""
                onChange={(e) => {
                  const id = Number(e.target.value);
                  if (id) setExtras((prev) => [...prev, id]);
                }}
              >
                <option value="">Elegí una cuenta</option>
                {otras.map((c) => (
                  <option key={c.id} value={c.id}>
                    {nombreCon(c)}
                  </option>
                ))}
              </SelectNativo>
            </Campo>
          ) : null}

          <Campo label="Observaciones" id="arqueo-obs" ayuda="Opcional.">
            <Textarea
              id="arqueo-obs"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={2}
              placeholder="Ej: quedó un vale de $ 3.000 sin cargar."
            />
          </Campo>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbiertoChange(false)}>
            Cancelar
          </Button>
          <Button type="button" disabled={!puede} onClick={() => setPaso("confirmar")}>
            <ClipboardCheck aria-hidden="true" />
            Seguir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
